import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { X, Info, AlertTriangle, CheckCircle, Rocket } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeConfig = {
  info: { bg: 'bg-blue-600', icon: Info, text: 'text-white' },
  warning: { bg: 'bg-amber-500', icon: AlertTriangle, text: 'text-white' },
  success: { bg: 'bg-emerald-600', icon: CheckCircle, text: 'text-white' },
  release: { bg: 'bg-indigo-600', icon: Rocket, text: 'text-white' },
};

export default function AnnouncementBanner() {
  const { token } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const fetchAnnouncements = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAnnouncements(data);
        setCurrentIndex(0);
      }
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchAnnouncements();
    // Poll every 5 minutes for new announcements
    const interval = setInterval(fetchAnnouncements, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAnnouncements]);

  const handleDismiss = async (announcementId) => {
    try {
      await fetch(`${API_URL}/api/announcements/${announcementId}/dismiss`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
    } catch {}
  };

  if (announcements.length === 0) return null;

  const current = announcements[currentIndex];
  if (!current) return null;

  const config = typeConfig[current.type] || typeConfig.info;
  const Icon = config.icon;

  return (
    <div className={`${config.bg} ${config.text} px-4 py-2.5 relative z-[60]`} data-testid="announcement-banner">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Icon className="w-4 h-4 flex-shrink-0 opacity-90" />
          <div className="min-w-0 flex-1">
            <span className="font-semibold text-sm">{current.title}</span>
            <span className="text-sm opacity-90 ml-2">{current.message}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {announcements.length > 1 && (
            <span className="text-xs opacity-70">{currentIndex + 1}/{announcements.length}</span>
          )}
          {announcements.length > 1 && (
            <button
              onClick={() => setCurrentIndex(i => (i + 1) % announcements.length)}
              className="text-xs underline opacity-80 hover:opacity-100"
            >
              Next
            </button>
          )}
          <button
            onClick={() => handleDismiss(current.id)}
            className="p-1 rounded hover:bg-white/20 transition-colors"
            data-testid="dismiss-announcement"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
